import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { 
  Search, FileText, Calendar, MapPin, Building2, 
  ExternalLink, Bell, RefreshCw, Download, Filter,
  Clock, AlertCircle, CheckCircle, Loader2, FileDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PROVINCES = [
  'All Provinces',
  'National',
  'Eastern Cape',
  'Free State',
  'Gauteng',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'North West',
  'Northern Cape',
  'Western Cape'
];

const CATEGORIES = [
  'All Categories',
  'services',
  'goods',
  'works'
];

export default function TendersPage() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [province, setProvince] = useState('All Provinces');
  const [category, setCategory] = useState('All Categories');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  
  // Expiry filter
  const [expiryFilter, setExpiryFilter] = useState('all'); // all, 7days, 14days, 30days, custom
  const [customExpiryDate, setCustomExpiryDate] = useState('');
  
  // Alerts
  const [alerts, setAlerts] = useState(() => {
    const saved = localStorage.getItem('tenderAlerts');
    return saved ? JSON.parse(saved) : [];
  });
  const [alertKeywords, setAlertKeywords] = useState('');

  const fetchTenders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/tenders`, {
        params: {
          dateFrom,
          dateTo,
          pageSize: 100
        }
      });
      setTenders(response.data.releases || []);
    } catch (error) {
      console.error('Failed to fetch tenders:', error);
      toast.error('Failed to load tenders from eTenders API');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchTenders();
  }, [fetchTenders]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchTenders();
    setRefreshing(false);
    toast.success('Tenders refreshed');
  };

  const addAlert = () => {
    if (!alertKeywords.trim()) {
      toast.error('Please enter keywords for the alert');
      return;
    }
    const newAlerts = [...alerts, { id: Date.now(), keywords: alertKeywords, createdAt: new Date().toISOString() }];
    setAlerts(newAlerts);
    localStorage.setItem('tenderAlerts', JSON.stringify(newAlerts));
    setAlertKeywords('');
    toast.success('Alert created! You will be notified when matching tenders appear.');
  };

  const removeAlert = (alertId) => {
    const newAlerts = alerts.filter(a => a.id !== alertId);
    setAlerts(newAlerts);
    localStorage.setItem('tenderAlerts', JSON.stringify(newAlerts));
    toast.success('Alert removed');
  };

  // Filter tenders
  const filteredTenders = tenders.filter(tender => {
    const t = tender.tender || {};
    const matchesSearch = !search || 
      t.title?.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase()) ||
      t.procuringEntity?.name?.toLowerCase().includes(search.toLowerCase());
    
    const matchesProvince = province === 'All Provinces' || t.province === province;
    const matchesCategory = category === 'All Categories' || t.mainProcurementCategory === category;
    
    // Only show active/open tenders
    const endDate = t.tenderPeriod?.endDate ? new Date(t.tenderPeriod.endDate) : null;
    const isActive = t.status === 'active' || t.status === 'planning' || 
                     (endDate && endDate > new Date());
    
    // Expiry date filter
    let matchesExpiry = true;
    if (expiryFilter !== 'all' && endDate) {
      const now = new Date();
      const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      
      if (expiryFilter === '7days') {
        matchesExpiry = daysUntilExpiry <= 7 && daysUntilExpiry > 0;
      } else if (expiryFilter === '14days') {
        matchesExpiry = daysUntilExpiry <= 14 && daysUntilExpiry > 0;
      } else if (expiryFilter === '30days') {
        matchesExpiry = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
      } else if (expiryFilter === 'custom' && customExpiryDate) {
        const customDate = new Date(customExpiryDate);
        matchesExpiry = endDate <= customDate;
      }
    }
    
    return matchesSearch && matchesProvince && matchesCategory && isActive && matchesExpiry;
  });

  // Check for alert matches
  const alertMatches = filteredTenders.filter(tender => {
    const t = tender.tender || {};
    const text = `${t.title} ${t.description} ${t.procuringEntity?.name}`.toLowerCase();
    return alerts.some(alert => 
      alert.keywords.toLowerCase().split(',').some(kw => text.includes(kw.trim()))
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'planning':
        return <Badge className="bg-blue-100 text-blue-700">Planning</Badge>;
      case 'complete':
        return <Badge className="bg-gray-100 text-gray-700">Complete</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Export tenders to CSV
  const exportTendersCSV = () => {
    if (filteredTenders.length === 0) {
      toast.error('No tenders to export');
      return;
    }

    const headers = [
      'OCID',
      'Title',
      'Description',
      'Status',
      'Procuring Entity',
      'Province',
      'Category',
      'Value (ZAR)',
      'Published Date',
      'Closing Date',
      'Days Remaining',
      'Contact Name',
      'Contact Email',
      'Contact Phone',
      'Procurement Method'
    ];

    const rows = filteredTenders.map(release => {
      const t = release.tender || {};
      const daysRemaining = getDaysRemaining(t.tenderPeriod?.endDate);
      return [
        release.ocid || '',
        (t.title || '').replace(/,/g, ';').replace(/\n/g, ' '),
        (t.description || '').replace(/,/g, ';').replace(/\n/g, ' ').substring(0, 200),
        t.status || '',
        (t.procuringEntity?.name || '').replace(/,/g, ';'),
        t.province || '',
        t.mainProcurementCategory || '',
        t.value?.amount || '',
        t.tenderPeriod?.startDate ? formatDate(t.tenderPeriod.startDate) : '',
        t.tenderPeriod?.endDate ? formatDate(t.tenderPeriod.endDate) : '',
        daysRemaining !== null ? daysRemaining : '',
        (t.contactPerson?.name || '').replace(/,/g, ';'),
        t.contactPerson?.email || '',
        t.contactPerson?.telephoneNumber || '',
        t.procurementMethodDetails || t.procurementMethod || ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `tenders_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    toast.success(`Exported ${filteredTenders.length} tenders to CSV`);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tenders-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Live Tenders</h1>
            <p className="text-muted-foreground mt-1">
              Browse government tenders from eTenders South Africa
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportTendersCSV} data-testid="export-tenders">
              <FileDown className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={handleRefresh} disabled={refreshing} data-testid="refresh-tenders">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Alert Matches Banner */}
        {alertMatches.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="flex items-center gap-3 p-4">
              <Bell className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">
                  {alertMatches.length} tender{alertMatches.length !== 1 ? 's' : ''} match your alerts!
                </p>
                <p className="text-sm text-orange-700">
                  Check the highlighted tenders below
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tenders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-tenders-input"
                />
              </div>
              <Select value={province} onValueChange={setProvince}>
                <SelectTrigger data-testid="province-filter">
                  <SelectValue placeholder="Province" />
                </SelectTrigger>
                <SelectContent>
                  {PROVINCES.map(p => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Expiry Date Filter Row */}
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Closing:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  variant={expiryFilter === 'all' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setExpiryFilter('all')}
                  data-testid="expiry-all"
                >
                  All
                </Button>
                <Button 
                  variant={expiryFilter === '7days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setExpiryFilter('7days')}
                  className={expiryFilter === '7days' ? '' : 'text-red-600 border-red-200 hover:bg-red-50'}
                  data-testid="expiry-7days"
                >
                  Within 7 Days
                </Button>
                <Button 
                  variant={expiryFilter === '14days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setExpiryFilter('14days')}
                  className={expiryFilter === '14days' ? '' : 'text-orange-600 border-orange-200 hover:bg-orange-50'}
                  data-testid="expiry-14days"
                >
                  Within 14 Days
                </Button>
                <Button 
                  variant={expiryFilter === '30days' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setExpiryFilter('30days')}
                  data-testid="expiry-30days"
                >
                  Within 30 Days
                </Button>
                <Button 
                  variant={expiryFilter === 'custom' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setExpiryFilter('custom')}
                  data-testid="expiry-custom"
                >
                  Custom Date
                </Button>
              </div>
              {expiryFilter === 'custom' && (
                <Input
                  type="date"
                  value={customExpiryDate}
                  onChange={(e) => setCustomExpiryDate(e.target.value)}
                  className="w-40"
                  data-testid="custom-expiry-date"
                />
              )}
            </div>
            
            {/* Date Range for API */}
            <div className="flex flex-wrap items-center gap-4 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Published between:</span>
              </div>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-40"
                  data-testid="date-from"
                />
                <span className="text-muted-foreground self-center">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-40"
                  data-testid="date-to"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Tenders List */}
          <div className="lg:col-span-3 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {loading ? 'Loading...' : `${filteredTenders.length} live tender${filteredTenders.length !== 1 ? 's' : ''} found`}
              </p>
            </div>

            {loading ? (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </CardContent>
              </Card>
            ) : filteredTenders.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-medium mb-2">No tenders found</h3>
                  <p className="text-muted-foreground">Try adjusting your filters or date range</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredTenders.map((release) => {
                  const t = release.tender || {};
                  const daysRemaining = getDaysRemaining(t.tenderPeriod?.endDate);
                  const isAlertMatch = alerts.some(alert => 
                    `${t.title} ${t.description} ${t.procuringEntity?.name}`.toLowerCase()
                      .includes(alert.keywords.toLowerCase())
                  );

                  return (
                    <Card 
                      key={release.ocid} 
                      className={`hover:shadow-md transition-shadow cursor-pointer ${isAlertMatch ? 'border-orange-300 bg-orange-50/50' : ''}`}
                      onClick={() => {
                        setSelectedTender(release);
                        setShowDetailDialog(true);
                      }}
                      data-testid={`tender-card-${release.ocid}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              {getStatusBadge(t.status)}
                              {isAlertMatch && (
                                <Badge className="bg-orange-100 text-orange-700">
                                  <Bell className="h-3 w-3 mr-1" />
                                  Alert Match
                                </Badge>
                              )}
                              {daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0 && (
                                <Badge className="bg-red-100 text-red-700">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} left
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-lg mb-1 line-clamp-2">{t.title || 'Untitled Tender'}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                              {t.description || 'No description available'}
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Building2 className="h-4 w-4" />
                                {t.procuringEntity?.name || 'Unknown'}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {t.province || 'National'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                Closes: {formatDate(t.tenderPeriod?.endDate)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground mb-1">Value</p>
                            <p className="font-semibold">
                              {t.value?.amount ? formatCurrency(t.value.amount) : 'TBD'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Alerts Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Bell className="h-5 w-5" />
                  Tender Alerts
                </CardTitle>
                <CardDescription>
                  Get notified when tenders match your keywords
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="alert-keywords">Keywords (comma-separated)</Label>
                  <Input
                    id="alert-keywords"
                    placeholder="e.g., transport, logistics, delivery"
                    value={alertKeywords}
                    onChange={(e) => setAlertKeywords(e.target.value)}
                    data-testid="alert-keywords-input"
                  />
                </div>
                <Button onClick={addAlert} className="w-full" data-testid="add-alert-button">
                  <Bell className="h-4 w-4 mr-2" />
                  Add Alert
                </Button>

                {alerts.length > 0 && (
                  <div className="space-y-2 pt-4 border-t">
                    <p className="text-sm font-medium">Active Alerts</p>
                    {alerts.map(alert => (
                      <div key={alert.id} className="flex items-center justify-between bg-muted/50 rounded-lg p-2">
                        <span className="text-sm truncate flex-1">{alert.keywords}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAlert(alert.id)}
                          className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Loaded</span>
                  <span className="font-medium">{tenders.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Live/Active</span>
                  <span className="font-medium text-green-600">{filteredTenders.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Alert Matches</span>
                  <span className="font-medium text-orange-600">{alertMatches.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Closing Soon (7 days)</span>
                  <span className="font-medium text-red-600">
                    {filteredTenders.filter(r => {
                      const days = getDaysRemaining(r.tender?.tenderPeriod?.endDate);
                      return days !== null && days <= 7 && days > 0;
                    }).length}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tender Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            {selectedTender && (
              <>
                <DialogHeader>
                  <DialogTitle>{selectedTender.tender?.title || 'Tender Details'}</DialogTitle>
                  <DialogDescription>
                    OCID: {selectedTender.ocid}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Status & Dates */}
                  <div className="flex flex-wrap gap-2">
                    {getStatusBadge(selectedTender.tender?.status)}
                    <Badge variant="outline">
                      {selectedTender.tender?.procurementMethodDetails || selectedTender.tender?.procurementMethod}
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {selectedTender.tender?.mainProcurementCategory}
                    </Badge>
                  </div>

                  {/* Description */}
                  <div>
                    <h4 className="font-semibold mb-2">Description</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {selectedTender.tender?.description || 'No description available'}
                    </p>
                  </div>

                  <Separator />

                  {/* Key Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold mb-2">Procuring Entity</h4>
                      <p className="text-sm">{selectedTender.tender?.procuringEntity?.name}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Province</h4>
                      <p className="text-sm">{selectedTender.tender?.province || 'National'}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Value</h4>
                      <p className="text-sm font-medium">
                        {selectedTender.tender?.value?.amount 
                          ? formatCurrency(selectedTender.tender.value.amount)
                          : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Category</h4>
                      <p className="text-sm">{selectedTender.tender?.category || 'N/A'}</p>
                    </div>
                  </div>

                  <Separator />

                  {/* Dates */}
                  <div>
                    <h4 className="font-semibold mb-2">Important Dates</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Published:</span>{' '}
                        {formatDate(selectedTender.tender?.tenderPeriod?.startDate)}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Closing:</span>{' '}
                        <span className="font-medium text-red-600">
                          {formatDate(selectedTender.tender?.tenderPeriod?.endDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Briefing Session */}
                  {selectedTender.tender?.briefingSession?.isSession && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-2">Briefing Session</h4>
                        <div className="text-sm space-y-1">
                          <p>
                            <span className="text-muted-foreground">Date:</span>{' '}
                            {formatDate(selectedTender.tender.briefingSession.date)}
                          </p>
                          <p>
                            <span className="text-muted-foreground">Venue:</span>{' '}
                            {selectedTender.tender.briefingSession.venue}
                          </p>
                          <p>
                            <span className="text-muted-foreground">Compulsory:</span>{' '}
                            {selectedTender.tender.briefingSession.compulsory ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Contact */}
                  {selectedTender.tender?.contactPerson && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-2">Contact Person</h4>
                        <div className="text-sm space-y-1">
                          <p>{selectedTender.tender.contactPerson.name}</p>
                          <p className="text-muted-foreground">
                            {selectedTender.tender.contactPerson.email}
                          </p>
                          <p className="text-muted-foreground">
                            {selectedTender.tender.contactPerson.telephoneNumber}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Documents */}
                  {selectedTender.tender?.documents?.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h4 className="font-semibold mb-2">Documents</h4>
                        <div className="space-y-2">
                          {selectedTender.tender.documents.map((doc, idx) => (
                            <a
                              key={idx}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                            >
                              <Download className="h-4 w-4" />
                              {doc.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                    Close
                  </Button>
                  <a
                    href={`https://www.etenders.gov.za/Home/TenderOpportunities?id=${selectedTender.tender?.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View on eTenders
                    </Button>
                  </a>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
